import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../styles/diario.css";

const API_URL = process.env.REACT_APP_API_URL;

const initialForm = {
  ID_CLIENTE: "",
  ID_TP_OCORRENCIA: "",
  ID_SEVERIDADE: "",
  ID_STATUS_OCORRENCIA: "",
  DATA_OCORRENCIA: "",
  MOTIVO: "",
  DESCRICAO_OCORRENCIA: "",
  IMPACTO_NS: "",
  FILAS_IMPACTADAS: "",
  QTD_AGENTES_AFETADOS: 0,
  IMPACTO_DESCRICAO: "",
  ACAO_TOMADA: "",
  CHAMADO_ABERTO: false,
  NUMERO_CHAMADO: "",
  PENDENCIAS_PROX_TURNO: "",
  RECOMENDACAO_SUPERVISOR: "",
  NEC_COMUNIC_CLIENTE: "",
  OBSERVACOES: "",
};

const filaOptions = [
  "Todas",
  "Telefonia",
  "e-mail",
  "Whatsapp",
  "Chat",
  "CRM",
  "Backoffice",
  "Reclame aqui",
];

const formatDateInput = (date) => {
  const pad = (v) => String(v).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

const normalizeString = (str) => {
  const base = String(str ?? "");
  return base
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const normalizeDateRangeForApi = (rawFilters = {}) => {
  const normalizeStart = (value) => {
    if (!value) return "";
    if (value.includes(" ") || value.includes("T")) return value;
    return `${value} 00:00:00`;
  };
  const normalizeEnd = (value) => {
    if (!value) return "";
    if (value.includes(" ") || value.includes("T")) return value;
    return `${value} 23:59:59`;
  };

  return {
    ...rawFilters,
    from: normalizeStart(rawFilters.from),
    to: normalizeEnd(rawFilters.to),
  };
};

const buildDefaultFilters = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    clienteId: "",
    tipoOcorrenciaId: "",
    severidadeId: "",
    statusOcorrenciaId: "",
    search: "",
    from: formatDateInput(start),
    to: formatDateInput(end),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const isResolved = (statusText) =>
  normalizeString(statusText || "").includes("resolvid");

const formatTempoResolucao = (inicio, fim, minutosApi) => {
  const computeMinutes = () => {
    if (!inicio || !fim) return null;
    const diffMs = new Date(fim) - new Date(inicio);
    if (Number.isNaN(diffMs)) return null;
    const minutes = Math.round(diffMs / 60000);
    return minutes >= 0 ? minutes : null;
  };

  const minutes = minutosApi ?? computeMinutes();
  if (minutes === null || minutes === undefined) return "-";
  if (minutes < 0) return "-";

  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(" ");
};

const Diario = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [refe, setRefe] = useState({
    clientes: [],
    tipos: [],
    severidades: [],
    status: [],
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [filters, setFilters] = useState(buildDefaultFilters);

  const isAdminLike = useMemo(() => [1, 2, 4].includes(Number(role)), [role]);
  const isOperacao = useMemo(() => Number(role) === 3, [role]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (!decoded?.ID_TP_USER || decoded.ID_TP_USER === 5) {
        toast.error("Acesso não permitido ao Diário de Bordo.");
        navigate("/dashs");
        return;
      }
      setRole(decoded.ID_TP_USER);
      loadReferencias(token, decoded);
      loadList(token, decoded, 1, filters);
    } catch (err) {
      console.error(err);
      toast.error("Sessão inválida. Faça login novamente.");
      navigate("/login");
    }
  }, []);

  const loadReferencias = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/api/diario/referencias`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefe(
        res.data || { clientes: [], tipos: [], severidades: [], status: [] }
      );
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar referências.");
    }
  };

  const loadList = async (
    token,
    decoded,
    targetPage = 1,
    extraFilters = {}
  ) => {
    setLoading(true);
    try {
      const dateFilters = normalizeDateRangeForApi(extraFilters);
      const params = {
        page: targetPage,
        pageSize,
        ...dateFilters,
      };
      const res = await axios.get(`${API_URL}/api/diario`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Erro ao listar ocorrências.");
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const token = localStorage.getItem("authToken");
    if (!token) return navigate("/login");
    loadList(token, null, 1, filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = buildDefaultFilters();
    setFilters(defaultFilters);
    const token = localStorage.getItem("authToken");
    if (!token) return navigate("/login");
    loadList(token, null, 1, defaultFilters);
  };

  const openNew = () => {
    setEditId(null);
    setForm(initialForm);
  };

  const handleEdit = async (id) => {
    const token = localStorage.getItem("authToken");
    if (!token) return navigate("/login");
    try {
      const res = await axios.get(`${API_URL}/api/diario/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      setEditId(id);
      setForm({
        ...initialForm,
        ...data,
        DATA_OCORRENCIA: data.DATA_OCORRENCIA
          ? data.DATA_OCORRENCIA.slice(0, 16)
          : "",
        QTD_AGENTES_AFETADOS:
          Number(
            data.QTD_AGENTES_AFETADOS ?? initialForm.QTD_AGENTES_AFETADOS
          ),
      });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Erro ao carregar ocorrência.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");
    if (!token) return navigate("/login");

    const payload = {
      ...form,
      CHAMADO_ABERTO: !!form.CHAMADO_ABERTO,
      QTD_AGENTES_AFETADOS: Number(form.QTD_AGENTES_AFETADOS || 0),
    };

    try {
      if (editId) {
        await axios.put(`${API_URL}/api/diario/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Ocorrência atualizada.");
      } else {
        await axios.post(`${API_URL}/api/diario`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Ocorrência criada.");
      }
      setForm(initialForm);
      setEditId(null);
      loadList(token, null, page, filters);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Erro ao salvar.");
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="diario-container">
      <header className="diario-header">
        <div>
          <h1>Diário de Bordo</h1>
          <p className="muted">
            {isAdminLike
              ? "Visualização completa"
              : isOperacao
              ? "Todas as ocorrências"
              : ""}
          </p>
        </div>
        <div className="actions">
          <button className="btn secondary" onClick={() => navigate("/dashs")}>
            Voltar
          </button>
          {/* <button className="btn primary" onClick={openNew}>
            Nova ocorrência */}
          {/* </button> */}
        </div>
      </header>

      <section className="card filters-card">
        <div className="filters-grid">
          <div>
            <label>Cliente</label>
            <select
              value={filters.clienteId}
              onChange={(e) =>
                setFilters((p) => ({ ...p, clienteId: e.target.value }))
              }
            >
              <option value="">Todos</option>
              {refe.clientes.map((c) => (
                <option key={c.ID_CLIENTE} value={c.ID_CLIENTE}>
                  {c.NOME_CLIENTE}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Tipo</label>
            <select
              value={filters.tipoOcorrenciaId}
              onChange={(e) =>
                setFilters((p) => ({ ...p, tipoOcorrenciaId: e.target.value }))
              }
            >
              <option value="">Todos</option>
              {refe.tipos.map((t) => (
                <option key={t.ID_TP_OCORRENCIA} value={t.ID_TP_OCORRENCIA}>
                  {t.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Severidade</label>
            <select
              value={filters.severidadeId}
              onChange={(e) =>
                setFilters((p) => ({ ...p, severidadeId: e.target.value }))
              }
            >
              <option value="">Todas</option>
              {refe.severidades.map((s) => (
                <option key={s.ID_SEVERIDADE} value={s.ID_SEVERIDADE}>
                  {s.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Status</label>
            <select
              value={filters.statusOcorrenciaId}
              onChange={(e) =>
                setFilters((p) => ({
                  ...p,
                  statusOcorrenciaId: e.target.value,
                }))
              }
            >
              <option value="">Todos</option>
              {refe.status.map((s) => (
                <option
                  key={s.ID_STATUS_OCORRENCIA}
                  value={s.ID_STATUS_OCORRENCIA}
                >
                  {s.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>De</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) =>
                setFilters((p) => ({ ...p, from: e.target.value }))
              }
            />
          </div>
          <div>
            <label>Até</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) =>
                setFilters((p) => ({ ...p, to: e.target.value }))
              }
            />
          </div>
          <div>
            <label>Buscar</label>
            <input
              type="text"
              placeholder="Motivo ou descrição"
              value={filters.search}
              onChange={(e) =>
                setFilters((p) => ({ ...p, search: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="filter-actions">
          <button className="btn secondary" onClick={handleClearFilters}>
            Limpar
          </button>
          <button className="btn primary" onClick={handleFilter}>
            Filtrar
          </button>
        </div>
      </section>

      <section className="card">
        <div className="table-header">
          <span>
            {loading ? "Carregando..." : `${total} registro(s)`} — Página {page}{" "}
            de {pages}
          </span>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Cliente</th>
                <th>Motivo</th>
                <th>Tipo</th>
                <th>Severidade</th>
                <th>Status</th>
                <th>Resolvido em</th>
                <th>Tempo para resolver</th>
                <th>Registrado por</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted center">
                    Nenhuma ocorrência encontrada.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr key={it.ID_DIARIO}>
                  <td>
                    {it.DATA_OCORRENCIA
                      ? new Date(it.DATA_OCORRENCIA).toLocaleString()
                      : ""}
                  </td>
                  <td>{it.NOME_CLIENTE || "-"}</td>
                  <td>{it.MOTIVO}</td>
                  <td>{it.TIPO_OCORRENCIA || "-"}</td>
                  <td>{it.SEVERIDADE || "-"}</td>
                  <td>{it.STATUS_OCORRENCIA || "-"}</td>
                  <td>
                    {isResolved(it.STATUS_OCORRENCIA)
                      ? formatDateTime(it.DATA_RESOLUCAO)
                      : "-"}
                  </td>
                  <td>
                    {isResolved(it.STATUS_OCORRENCIA)
                      ? formatTempoResolucao(
                          it.DATA_OCORRENCIA,
                          it.DATA_RESOLUCAO,
                          it.TEMPO_RESOLUCAO_MINUTOS
                        )
                      : "-"}
                  </td>
                  <td>{it.USUARIO_REGISTRO || "-"}</td>
                  <td>
                    <button
                      className="btn small"
                      onClick={() => handleEdit(it.ID_DIARIO)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <button
            className="btn secondary small"
            onClick={() => {
              const token = localStorage.getItem("authToken");
              const newPage = Math.max(1, page - 1);
              setPage(newPage);
              loadList(token, null, newPage, filters);
            }}
            disabled={page <= 1}
          >
            Anterior
          </button>
          <button
            className="btn secondary small"
            onClick={() => {
              const token = localStorage.getItem("authToken");
              const newPage = Math.min(pages, page + 1);
              setPage(newPage);
              loadList(token, null, newPage, filters);
            }}
            disabled={page >= pages}
          >
            Próxima
          </button>
        </div>
      </section>

      <section className="card">
        <h3>{editId ? "Editar ocorrência" : "Nova ocorrência"}</h3>
        <form className="form-grid" onSubmit={handleSubmit}>
          <div>
            <label>Cliente *</label>
            <select
              value={form.ID_CLIENTE}
              onChange={(e) =>
                setForm((p) => ({ ...p, ID_CLIENTE: e.target.value }))
              }
              required
            >
              <option value="">Todos</option>
              {refe.clientes.map((c) => (
                <option key={c.ID_CLIENTE} value={c.ID_CLIENTE}>
                  {c.NOME_CLIENTE}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Tipo *</label>
            <select
              value={form.ID_TP_OCORRENCIA}
              onChange={(e) =>
                setForm((p) => ({ ...p, ID_TP_OCORRENCIA: e.target.value }))
              }
              required
            >
              <option value="">Selecione</option>
              {refe.tipos.map((t) => (
                <option key={t.ID_TP_OCORRENCIA} value={t.ID_TP_OCORRENCIA}>
                  {t.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Severidade *</label>
            <select
              value={form.ID_SEVERIDADE}
              onChange={(e) =>
                setForm((p) => ({ ...p, ID_SEVERIDADE: e.target.value }))
              }
              required
            >
              <option value="">Selecione</option>
              {refe.severidades.map((s) => (
                <option key={s.ID_SEVERIDADE} value={s.ID_SEVERIDADE}>
                  {s.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Status *</label>
            <select
              value={form.ID_STATUS_OCORRENCIA}
              onChange={(e) =>
                setForm((p) => ({ ...p, ID_STATUS_OCORRENCIA: e.target.value }))
              }
              required
            >
              <option value="">Selecione</option>
              {refe.status.map((s) => (
                <option
                  key={s.ID_STATUS_OCORRENCIA}
                  value={s.ID_STATUS_OCORRENCIA}
                >
                  {s.DESCRICAO}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Data ocorrência *</label>
            <input
              type="datetime-local"
              value={form.DATA_OCORRENCIA}
              onChange={(e) =>
                setForm((p) => ({ ...p, DATA_OCORRENCIA: e.target.value }))
              }
              required
            />
          </div>
          <div>
            <label>Motivo *</label>
            <input
              type="text"
              value={form.MOTIVO}
              onChange={(e) =>
                setForm((p) => ({ ...p, MOTIVO: e.target.value }))
              }
              required
            />
          </div>
          <div className="full">
            <label>Descrição</label>
            <textarea
              value={form.DESCRICAO_OCORRENCIA}
              onChange={(e) =>
                setForm((p) => ({ ...p, DESCRICAO_OCORRENCIA: e.target.value }))
              }
            />
          </div>
          <div>
            <label>Impacto NS</label>
            <input
              type="text"
              value={form.IMPACTO_NS}
              onChange={(e) =>
                setForm((p) => ({ ...p, IMPACTO_NS: e.target.value }))
              }
            />
          </div>
          <div>
            <label>Filas impactadas</label>
            <select
              value={form.FILAS_IMPACTADAS}
              onChange={(e) =>
                setForm((p) => ({ ...p, FILAS_IMPACTADAS: e.target.value }))
              }
            >
              <option value="">Selecione</option>
              {filaOptions.map((fila) => (
                <option key={fila} value={fila}>
                  {fila}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Qtd agentes afetados</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.QTD_AGENTES_AFETADOS}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  QTD_AGENTES_AFETADOS: Math.max(0, Number(e.target.value)),
                }))
              }
            />
          </div>
          <div>
            <label>Impacto descrição</label>
            <input
              type="text"
              value={form.IMPACTO_DESCRICAO}
              onChange={(e) =>
                setForm((p) => ({ ...p, IMPACTO_DESCRICAO: e.target.value }))
              }
            />
          </div>
          <div className="full">
            <label>Ação tomada</label>
            <textarea
              value={form.ACAO_TOMADA}
              onChange={(e) =>
                setForm((p) => ({ ...p, ACAO_TOMADA: e.target.value }))
              }
            />
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                checked={form.CHAMADO_ABERTO}
                onChange={(e) =>
                  setForm((p) => ({ ...p, CHAMADO_ABERTO: e.target.checked }))
                }
              />{" "}
              Chamado aberto
            </label>
          </div>
          <div>
            <label>Número chamado</label>
            <input
              type="text"
              value={form.NUMERO_CHAMADO}
              onChange={(e) =>
                setForm((p) => ({ ...p, NUMERO_CHAMADO: e.target.value }))
              }
            />
          </div>
          <div className="full">
            <label>Pendências próximo turno</label>
            <textarea
              value={form.PENDENCIAS_PROX_TURNO}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  PENDENCIAS_PROX_TURNO: e.target.value,
                }))
              }
            />
          </div>
          <div className="full">
            <label>Recomendação supervisor</label>
            <textarea
              value={form.RECOMENDACAO_SUPERVISOR}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  RECOMENDACAO_SUPERVISOR: e.target.value,
                }))
              }
            />
          </div>
          <div>
            <label>Nec. comunicação cliente</label>
            <select
              value={form.NEC_COMUNIC_CLIENTE}
              onChange={(e) =>
                setForm((p) => ({ ...p, NEC_COMUNIC_CLIENTE: e.target.value }))
              }
            >
              <option value="">Selecione</option>
              <option value="Sim">Sim</option>
              <option value="Não">Não</option>
            </select>
          </div>
          <div className="full">
            <label>Observações</label>
            <textarea
              value={form.OBSERVACOES}
              onChange={(e) =>
                setForm((p) => ({ ...p, OBSERVACOES: e.target.value }))
              }
            />
          </div>
        </form>
        <div className="form-actions">
          <button className="btn secondary" onClick={openNew}>
            Cancelar
          </button>
          <button className="btn primary" onClick={handleSubmit}>
            {editId ? "Salvar alterações" : "Criar ocorrência"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Diario;
